// admin-panel/src/lib/api.js
const BASE_URL = import.meta.env.VITE_API_URL || 'https://api.decentwoods.com/api/v1';

class ApiClient {
  constructor() {
    this.baseUrl = BASE_URL;
  }

  getToken() {
    return localStorage.getItem('dw_token');
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const config = {
      headers: {
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    // Don't set Content-Type for FormData (let browser set it with boundary)
    if (!(options.body instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, config);

    if (response.status === 401) {
      localStorage.removeItem('dw_token');
      window.location.href = '/login';
      return;
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new Error(data.message || 'Request failed');
      error.status = response.status;
      error.errors = data.errors;
      throw error;
    }

    return data;
  }

  get(endpoint, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`${endpoint}${query ? '?' + query : ''}`);
  }

  post(endpoint, data, isFormData = false) {
    return this.request(endpoint, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
    });
  }

  put(endpoint, data, isFormData = false) {
    if (isFormData) {
      data.append('_method', 'PUT');
      return this.request(endpoint, { method: 'POST', body: data });
    }
    return this.request(endpoint, { method: 'PUT', body: JSON.stringify(data) });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  patch(endpoint, data) {
    return this.request(endpoint, { method: 'PATCH', body: JSON.stringify(data) });
  }
}

export const api = new ApiClient();

// ─── Auth ──────────────────────────────────────────────────
export const authApi = {
  login:          (data) => api.post('/auth/login', data),
  logout:         ()     => api.post('/auth/logout'),
  me:             ()     => api.get('/auth/me'),
  updateProfile:  (data) => api.put('/auth/profile', data, data instanceof FormData),
  changePassword: (data) => api.put('/auth/password', data),
};

// ─── Dashboard ─────────────────────────────────────────────
export const dashboardApi = {
  stats:            () => api.get('/dashboard/stats'),
  charts:           () => api.get('/dashboard/charts'),
  recentInquiries:  () => api.get('/dashboard/recent-inquiries'),
  notifications:    () => api.get('/dashboard/notifications'),
  markRead:         (id) => api.post(`/dashboard/notifications/${id}/read`),
};

// ─── Projects ──────────────────────────────────────────────
export const projectsApi = {
  list:          (params) => api.get('/admin/projects', params),
  get:           (id)     => api.get(`/admin/projects/${id}`),
  create:        (data)   => api.post('/admin/projects', data, data instanceof FormData),
  update:        (id, d)  => api.put(`/admin/projects/${id}`, d, d instanceof FormData),
  delete:        (id)     => api.delete(`/admin/projects/${id}`),
  uploadImages:  (id, fd) => api.post(`/admin/projects/${id}/images`, fd, true),
  deleteImage:   (pid, iid) => api.delete(`/admin/projects/${pid}/images/${iid}`),
  addVideo:      (id, d)  => api.post(`/admin/projects/${id}/videos`, d, d instanceof FormData),
  deleteVideo:   (pid, vid) => api.delete(`/admin/projects/${pid}/videos/${vid}`),
  toggleFeatured:(id)     => api.post(`/admin/projects/${id}/toggle-featured`),
  categories:    ()       => api.get('/categories'),
};

// ─── Inquiries ─────────────────────────────────────────────
export const inquiriesApi = {
  list:       (params)      => api.get('/admin/inquiries', params),
  stats:      ()            => api.get('/admin/inquiries/stats'),
  get:        (id)          => api.get(`/admin/inquiries/${id}`),
  update:     (id, data)    => api.put(`/admin/inquiries/${id}`, data),
  delete:     (id)          => api.delete(`/admin/inquiries/${id}`),
  addNote:    (id, data)    => api.post(`/admin/inquiries/${id}/notes`, data),
  sendWhatsApp:(id, data)   => api.post(`/admin/inquiries/${id}/whatsapp`, data),
  markRead:   (id)          => api.post(`/admin/inquiries/${id}/mark-read`),
};

// ─── Quotes ────────────────────────────────────────────────
export const quotesApi = {
  list:        (params)   => api.get('/admin/quotes', params),
  get:         (id)       => api.get(`/admin/quotes/${id}`),
  create:      (data)     => api.post('/admin/quotes', data),
  update:      (id, data) => api.put(`/admin/quotes/${id}`, data),
  delete:      (id)       => api.delete(`/admin/quotes/${id}`),
  send:        (id)       => api.post(`/admin/quotes/${id}/send`),
  sendWhatsApp:(id)       => api.post(`/admin/quotes/${id}/whatsapp`),
  calculate:   (params)   => api.get('/quotes/calculate', params),
};

// ─── Services ──────────────────────────────────────────────
export const servicesApi = {
  list:   (params) => api.get('/admin/services', params),
  get:    (id)     => api.get(`/admin/services/${id}`),
  create: (data)   => api.post('/admin/services', data, data instanceof FormData),
  update: (id, d)  => api.put(`/admin/services/${id}`, d, d instanceof FormData),
  delete: (id)     => api.delete(`/admin/services/${id}`),
};

// ─── Testimonials ──────────────────────────────────────────
export const testimonialsApi = {
  list:   (params) => api.get('/admin/testimonials', params),
  create: (data)   => api.post('/admin/testimonials', data),
  update: (id, d)  => api.put(`/admin/testimonials/${id}`, d),
  delete: (id)     => api.delete(`/admin/testimonials/${id}`),
};

// ─── Media ─────────────────────────────────────────────────
export const mediaApi = {
  list:       (params) => api.get('/admin/media', params),
  upload:     (fd)     => api.post('/admin/media/upload', fd, true),
  update:     (id, d)  => api.put(`/admin/media/${id}`, d),
  delete:     (id)     => api.delete(`/admin/media/${id}`),
  bulkDelete: (ids)    => api.post('/admin/media/bulk-delete', { ids }),
};

// ─── CMS ───────────────────────────────────────────────────
export const cmsApi = {
  list:   ()       => api.get('/admin/cms'),
  update: (id, d)  => api.put(`/admin/cms/${id}`, d),
  create: (data)   => api.post('/admin/cms', data),
};

// ─── Settings ──────────────────────────────────────────────
export const settingsApi = {
  list:        () => api.get('/admin/settings'),
  bulkUpdate:  (data) => api.put('/admin/settings', data),
};

// ─── Categories ────────────────────────────────────────────
export const categoriesApi = {
  list:   ()       => api.get('/admin/categories'),
  create: (data)   => api.post('/admin/categories', data),
  update: (id, d)  => api.put(`/admin/categories/${id}`, d),
  delete: (id)     => api.delete(`/admin/categories/${id}`),
};

// ─── Blog ───────────────────────────────────────────────────
export const blogApi = {
  // Admin
  list:           (params) => api.get('/admin/blog', params),
  get:            (id)     => api.get(`/admin/blog/${id}`),
  create:         (data)   => api.post('/admin/blog', data, data instanceof FormData),
  update:         (id, d)  => api.put(`/admin/blog/${id}`, d, d instanceof FormData),
  delete:         (id)     => api.delete(`/admin/blog/${id}`),
  // Blog Categories
  listCategories: ()       => api.get('/blog/categories'),
  createCategory: (data)   => api.post('/admin/blog-categories', data),
  updateCategory: (id, d)  => api.put(`/admin/blog-categories/${id}`, d),
  deleteCategory: (id)     => api.delete(`/admin/blog-categories/${id}`),
};
